import React from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { globalHistory } from '@reach/router';

import H3 from '@rotaready/frecl/build/H3';
import Text from '@rotaready/frecl/build/Text';
import FormInputWrapper from '@rotaready/frecl/build/FormInputWrapper';
import TextInput from '@rotaready/frecl/build/TextInput';
import Button from '@rotaready/frecl/build/Button';
import Checkbox from '@rotaready/frecl/build/Checkbox';
import LinkWithArrow from '@rotaready/frecl/build/LinkWithArrow';
import PropTypes from 'prop-types';
import Dropdown from '@rotaready/frecl/build/Dropdown';

const isBrowser = typeof window !== 'undefined';

const Container = styled.div``;

const FormInnerWrapper = styled.div`
  padding: 40px;
  border-radius: 3px;
  border: solid 1px ${({ theme: { colors } }) => colors.grey20};
  box-shadow: 0 0 4px 1px ${({ theme: { colors } }) => colors.boxshadowmedium};
`;

const Heading = styled(H3).attrs({
  uistyle: 'brand160',
})`
  padding-bottom: 40px;
`;

const SuccessWrapper = styled.div`
  margin: 20px;
`;

const TextBlock = styled.div`
  margin-bottom: 20px;
`;

const siteCountOptions = [
  { value: '1-4', label: '1-4' },
  { value: '5-9', label: '5-9' },
  { value: '10-19', label: '10-19' },
  { value: '20-49', label: '20-49' },
  { value: '50-99', label: '50-99' },
  { value: '100-199', label: '100-199' },
  { value: '200-499', label: '200-499' },
  { value: '500-999', label: '500-999' },
  { value: '1000-4999', label: '1000-4999' },
  { value: '>=5000', label: '>=5000' },
];

class ContactForm extends React.Component {
  static propTypes = {
    title: PropTypes.string,
    showComments: PropTypes.bool,
    showPhone: PropTypes.bool,
    commentsLabel: PropTypes.string,
    submitLabel: PropTypes.string,
    source: PropTypes.string,
    label: PropTypes.string.isRequired,
    checkboxes: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
      required: PropTypes.bool,
    })),
    onSuccess: PropTypes.node,
    marketoProgramName: PropTypes.string,
    initialFirstName: PropTypes.string,
    initialLastName: PropTypes.string,
    initialEmail: PropTypes.string,
  };

  static defaultProps = {
    title: 'Get in touch',
    showComments: false,
    showPhone: false,
    commentsLabel: 'Let us know exactly how we can help',
    submitLabel: 'Send details',
    source: 'contact_us',
    onSuccess: (
      <>
        <TextBlock>
          <Text size="lg" text="Thanks! We're really grateful for you taking the time to reach out. Someone from the Rotaready team will give you a call as soon as they can." />
        </TextBlock>

        <TextBlock>
          <Text text="While you're here, have you read about why we started Rotaready?" size="lg" />
        </TextBlock>

        <TextBlock>
          <LinkWithArrow
            direction="right"
            to="/about"
            text="See the Rotaready story"
            render={props => <Link {...props} />}
          />
        </TextBlock>
      </>
    ),
    marketoProgramName: '',
    initialFirstName: '',
    initialLastName: '',
    initialEmail: '',
  };

  constructor(props) {
    super(props);

    const {
      showPhone,
      checkboxes,
    } = props;

    this.formTopRef = React.createRef();

    this.initialValues = {
      firstName: '',
      lastName: '',
      company: '',
      email: '',
      phone: '',
      comments: '',
      jobTitle: '',
      siteCount: '',
    };

    const schema = {
      firstName: yup
        .string()
        .required('Please tell us your first name.')
        .min(2, 'Your first name is a little too short.')
        .max(50, 'Your first name is a little too long.'),
      lastName: yup
        .string()
        .required('Please tell us your last name.')
        .min(2, 'Your last name is a little too short.')
        .max(50, 'Your last name is a little too long.'),
      company: yup
        .string()
        .required('Please tell us your company name.')
        .min(3, 'Your company name is a little too short.')
        .max(50, 'Your company name is a little too long.'),
      email: yup
        .string()
        .required('Please tell us your email address - we promise to keep it private!')
        .email('Your email address looks a little unusual!'),
      phone: yup
        .string()
        .min(9, 'Your phone number is a little too short.')
        .max(18, 'Your phone number is a little too long.'),
      comments: yup
        .string()
        .max(255, 'Sadly that\'s slightly too much text!'),
      jobTitle: yup
        .string()
        .required('Please tell us your job title.')
        .min(2, 'Your job title is a little too short.')
        .max(50, 'Your job title is a little too long.'),
      siteCount: yup
        .string()
        .required('Please tell us how many venues your organisation has.')
    };

    if (showPhone) {
      schema.phone.required('We need your phone number - we won\'t share it with anyone, we promise.')
    }

    if (checkboxes) {
      checkboxes.forEach(c => {
        const { id, required, initial } = c;
        let schemaAddition = yup.boolean();

        if (required) {
          schemaAddition = schemaAddition.oneOf([true], 'You must tick this box to proceed.');
        }

        schema[id] = schemaAddition;

        if (initial !== undefined) {
          this.initialValues[id] = initial;
        } else {
          this.initialValues[id] = false;
        }
      });
    }

    this.schema = yup.object().shape(schema);

    this.handleSubmit = this.handleSubmit.bind(this);

    this.state = {
      formSubmitted: false,
    };
  }

  handleSubmit(values, actions) {
    const {
      showComments,
      showPhone,
      source,
      label,
      checkboxes,
      marketoProgramName,
    } = this.props;

    // Subtract sticky nav height
    const scrollTo = this.formTopRef.current.offsetTop - 80;

    const {
      firstName,
      lastName,
      company,
      email,
      phone,
      comments,
      jobTitle,
      siteCount,
    } = values;

    let gdprConsent = false;

    if (checkboxes) {
      const consentId = 'marketing';
      const consentCheckbox = checkboxes.find(({ id }) => id === consentId);

      if (consentCheckbox && values[consentId]) {
        gdprConsent = true;
      }
    }

    const { location } = globalHistory;

    const body = {
      email,
      company,
      job_title: jobTitle,
      phone: showPhone ? phone : undefined,
      comments: showComments ? comments : undefined,
      first_name: firstName,
      last_name: lastName,
      gdpr_consent: gdprConsent,
      program_name: marketoProgramName,
      form_id: 10140,
      pageURL: `https://rotaready.com${location.pathname}`,
      queryString: location.search,
      siteCount,
    };

    const url = 'https://webhooks.eu.workato.com/webhooks/rest/c178a3af-b19f-4483-aed4-0e5e9dfdb8c0/mkto_form_fill';
    const eventAction = 'form_submission';

    fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body),
    })
      .then(() => {
        actions.setSubmitting(false);
        this.setState({ formSubmitted: true });

        if (isBrowser) {
          window.scroll({ top: scrollTo, left: 0, behavior: 'smooth' });

          if (window.ga) {
            window.ga('send', 'event', source, eventAction, label);
          }

          if (window.gtag) {
            window.gtag('event', eventAction, { label });
          }

          if (window.uetq) {
            window.uetq.push('event', eventAction, {
              event_category: source,
              event_label: label,
              event_value: 0,
            });
          }
        }
      })
      .catch(() => {
        actions.setSubmitting(false);
        this.setState({ formSubmitted: true });

        if (isBrowser) {
          window.scroll({ top: scrollTo, left: 0, behavior: 'smooth' });
        }
      });
  }

  render() {
    const {
      formSubmitted,
    } = this.state;

    const {
      title,
      showComments,
      showPhone,
      commentsLabel,
      submitLabel,
      checkboxes,
      onSuccess,
      initialFirstName,
      initialLastName,
      initialEmail,
    } = this.props;

    let initialValuesToUse = this.initialValues
    if (initialFirstName && initialLastName && initialEmail) {
      initialValuesToUse = {
        ...this.initialValues,
        firstName: initialFirstName,
        lastName: initialLastName,
        email: initialEmail,
      }
    }

    return (
      <Container innerRef={this.formTopRef}>
        <Formik
          initialValues={initialValuesToUse}
          onSubmit={this.handleSubmit}
          validationSchema={this.schema}
          enableReinitialize={true}
        >
          {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
              setFieldValue,
            }) => (
            <FormInnerWrapper>
              {title && (<Heading text={title} />)}

              {formSubmitted
              && (
                <SuccessWrapper>
                  {onSuccess}
                </SuccessWrapper>
              )}

              {!formSubmitted
              && (
                <Form>
                  <FormInputWrapper
                    label="First name"
                    width={400}
                    required
                    validationStyle="alert"
                    validationMessage={errors.firstName && touched.firstName && (<Text text={errors.firstName} />)}
                  >
                    <TextInput
                      name="firstName"
                      fullWidth
                      placeholder="Enter your first name"
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormInputWrapper>

                  <FormInputWrapper
                    label="Last name"
                    width={400}
                    required
                    validationStyle="alert"
                    validationMessage={errors.lastName && touched.lastName && (<Text text={errors.lastName} />)}
                  >
                    <TextInput
                      name="lastName"
                      fullWidth
                      placeholder="Enter your last name"
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormInputWrapper>

                  <FormInputWrapper
                    label="Company name"
                    width={400}
                    required
                    validationStyle="alert"
                    validationMessage={errors.company && touched.company && (<Text text={errors.company} />)}
                  >
                    <TextInput
                      name="company"
                      fullWidth
                      placeholder="Enter company name"
                      value={values.company}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormInputWrapper>

                  <FormInputWrapper
                    label="Job title"
                    width={400}
                    required
                    validationStyle="alert"
                    validationMessage={errors.jobTitle && touched.jobTitle && (<Text text={errors.jobTitle} />)}
                  >
                    <TextInput
                      name="jobTitle"
                      fullWidth
                      placeholder="Enter job title"
                      value={values.jobTitle}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormInputWrapper>

                  <FormInputWrapper
                    label="Email address"
                    width={400}
                    required
                    validationStyle="alert"
                    validationMessage={errors.email && touched.email && (<Text text={errors.email} />)}
                  >
                    <TextInput
                      name="email"
                      fullWidth
                      placeholder="Enter your email address"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormInputWrapper>

                  {showPhone
                  && (
                    <FormInputWrapper
                      label="Phone number"
                      width={400}
                      required
                      validationStyle="alert"
                      validationMessage={errors.phone && touched.phone && (<Text text={errors.phone} />)}
                    >
                      <TextInput
                        name="phone"
                        fullWidth
                        placeholder="Enter your phone number"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </FormInputWrapper>
                  )}

                  <FormInputWrapper
                    label="Number of venues"
                    width={400}
                    required
                    validationStyle="alert"
                    validationMessage={errors.siteCount && touched.siteCount && (<Text text={errors.siteCount} />)}
                  >
                    <Dropdown
                      options={siteCountOptions}
                      onChange={({ value }) => {
                        setFieldValue('siteCount', value);
                      }}
                      value={values.siteCount}
                      placeholder={{ initial: 'Select...' }}
                      size="md"
                      fullWidth={true}
                    />
                  </FormInputWrapper>

                  {showComments
                  && (
                    <FormInputWrapper
                      label={commentsLabel}
                      width={400}
                      validationStyle="alert"
                      validationMessage={errors.comments && touched.comments && (<Text text={errors.comments} />)}
                    >
                      <TextInput
                        name="comments"
                        fullWidth
                        placeholder="Type your message here"
                        value={values.comments}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </FormInputWrapper>
                  )}

                  {checkboxes && checkboxes.length
                  && checkboxes.map(c => (
                    <FormInputWrapper
                      width={400}
                      validationStyle="alert"
                      validationMessage={errors[c.id] && touched[c.id] && (<Text text={errors[c.id]} />)}
                    >
                      <Checkbox
                        id={c.id}
                        fullWidth
                        text={c.text}
                        checked={values[c.id]}
                        onChange={handleChange}
                        labelSide="right"
                      />
                    </FormInputWrapper>
                  ))}

                  <Button
                    type="submit"
                    uistyle="primary"
                    text={submitLabel}
                    loading={isSubmitting}
                  />
                </Form>
              )}
            </FormInnerWrapper>
          )}
        </Formik>
      </Container>
    );
  }
}

export default ContactForm;
